import { useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import { createModal, useModalize } from '@/components/modalize'
import { colors } from '@/styles'
import { H4, ParagraphMedium } from '@/components/text'
import RentedIcon from './mark-rented-icon.svg?react'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px 40px;
`

const ContentWrapper = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 500px;
`

const ModalTitle = styled(H4)`
  margin: 28px 0 20px;
`

const ModalFooter = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;

  button:first-child {
    margin-bottom: 20px;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 300px;
`

const ContractLink = styled.button`
  padding: 0;
  background: none;
  border: none;
  font-size: 14px;
  color: ${colors.link};
  cursor: pointer;
`

const MarkAsRentedModal = ({ listingId, handleInternal, history }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()

  const handleExternalTenant = async () => {
    const { response } = await dispatch.listing.markAsRentedWithoutInquiries(listingId)
    if (response?.ok) {
      history.push(`/tenant/${listingId}`)
    }
    close()
  }

  const handleSign = () => {
    history.push(`/listing/${listingId}/contract/create`)
    close()
  }

  const handleInternalTenant = () => {
    handleInternal()
    close()
  }

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  return (
    <Wrapper role="dialog">
      <CloseButton onClick={close} />
      <ContentWrapper>
        <RentedIcon />
        <ModalTitle>{t('l.listing.edit.mark_as_rented_modal.title', 'Mark as Rented')}</ModalTitle>
        <ParagraphMedium>
          <Trans i18nKey="l.listing.edit.mark_as_rented_modal.description">
            If you have already signed a contract elsewhere, you may mark this listing as rented. If
            not,{' '}
            <ContractLink type="button" onClick={handleSign}>
              sign a contract
            </ContractLink>{' '}
            using our template.
          </Trans>
        </ParagraphMedium>
      </ContentWrapper>
      <ModalFooter>
        <StyledButton theme="primary" onClick={handleInternalTenant}>
          {t(
            'l.listing.edit.mark_as_rented_modal.internal_tenant.button',
            'I found my tenant on liv.rent'
          )}
        </StyledButton>
        <StyledButton theme="secondary" onClick={handleExternalTenant}>
          {t(
            'l.listing.edit.mark_as_rented_modal.external_tenant.button',
            'I found my tenant elsewhere'
          )}
        </StyledButton>
      </ModalFooter>
    </Wrapper>
  )
}

export const showMarkAsRentedModal = createModal(MarkAsRentedModal, {
  width: '620px',
  maxWidth: '90%',
  mobileFullscreen: true
})
